import React, { useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AmountField from 'components/form/AmountField';
import CustomSelect, { SelectOption } from 'components/form/CustomSelect';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import HourField from 'components/form/HourField';
import PercentField from 'components/form/PercentField';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import TemplateGroupView, { TemplateGroup } from 'pages/contract/components/TemplateGroupView';
import { UserSession } from 'stores/AuthenticationStoreAdapter';
import {
  ChangeContractRequestParams,
  TemplateItemInput,
  TerminateContractRequestParams,
  changeContractRequest,
  terminateContractRequest,
} from 'stores/ContractRequestStoreAdapter';
import { ContractDetailResult, ContractStatus } from 'stores/ContractStoreAdapter';
import {
  GetContractTemplateResponse,
  getContractTemplate,
} from 'stores/ContractTemplateStoreAdapter';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

import ContractConfirmDialog, { ConfirmDialogResult } from './ContractConfirmDialog';
import ContractPasteButton from './ContractPasteButton';

type ContractDetailProps = {
  detail: ContractDetailResult;
  userSession: UserSession;
  users: UserResult[];
  onUpdated: () => void;
};

const ContractDetail: React.FC<ContractDetailProps> = ({
  detail,
  userSession,
  users,
  onUpdated,
}) => {
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [changeConfirmOpen, setChangeConfirmOpen] = useState(false);

  // マスタデータ（編集モード用）
  const [prefectures, setPrefectures] = useState<MasterTypeResult[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
  const [contractUnits, setContractUnits] = useState<MasterTypeResult[]>([]);
  const [templateData, setTemplateData] = useState<GetContractTemplateResponse | null>(null);
  const [masterLoaded, setMasterLoaded] = useState(false);

  // 編集フォーム値
  const [editPersonInChargeId, setEditPersonInChargeId] = useState<string | null>(null);
  const [editPrefectureId, setEditPrefectureId] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editContractTypeId, setEditContractTypeId] = useState('');
  const [editContractEndDate, setEditContractEndDate] = useState('');
  const [editCostAmount, setEditCostAmount] = useState('');
  const [editSalesAmount, setEditSalesAmount] = useState('');
  const [editOperationRate, setEditOperationRate] = useState('');
  const [editContractUnitId, setEditContractUnitId] = useState('');
  const [editAdjustmentUpperLimit, setEditAdjustmentUpperLimit] = useState('');
  const [editAdjustmentLowerLimit, setEditAdjustmentLowerLimit] = useState('');
  const [editNeedsOriginSheet, setEditNeedsOriginSheet] = useState(false);
  const [editOriginSheetDestination, setEditOriginSheetDestination] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [editTemplateValues, setEditTemplateValues] = useState<Record<string, string>>({});
  const [editTechStackIds, setEditTechStackIds] = useState<string[]>([]);

  // テンプレートデータを初回マウント時に取得（閲覧モードでもテンプレート項目を表示するため）
  useEffect(() => {
    if (!templateData) {
      getContractTemplate({ visibleOnly: true })
        .then((tmpl) => setTemplateData(tmpl))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const managers = useMemo(() => users.filter((u) => u.roles.includes('MANAGER')), [users]);
  const backOfficeUsers = useMemo(
    () => users.filter((u) => u.roles.includes('BACK_OFFICE')),
    [users],
  );

  const isPersonInCharge = detail.personInCharge.current.userId === userSession.userId;
  const isManager = detail.manager.userId === userSession.userId;
  const canOperate = isPersonInCharge || isManager;

  const canChange = detail.status === 'ACTIVE' && !detail.hasPendingRequest && canOperate;
  const canTerminate = detail.status === 'ACTIVE' && !detail.hasPendingRequest && canOperate;

  const handleTerminateConfirm = (data: ConfirmDialogResult) => {
    setTerminateDialogOpen(false);
    const params: TerminateContractRequestParams = {
      contractId: detail.contractId,
      applicantUserIds: data.applicantUserIds,
      destinationUserIds: data.destinationUserIds,
    };
    terminateContractRequest(params)
      .then(() => {
        successDefault('契約終了申請を提出しました');
        onUpdated();
      })
      .catch((e: Error) => errorDefault('契約終了申請に失敗しました', e.message));
  };

  // --- 編集モード ---

  // マスタデータ読み込み
  const loadMasterData = () => {
    if (masterLoaded) return Promise.resolve();
    return Promise.all([
      getMasterType('prefecture'),
      getMasterType('contract_type'),
      getMasterType('contract_unit'),
      getContractTemplate({ visibleOnly: true }),
    ])
      .then(([pref, ctype, cunit, tmpl]) => {
        setPrefectures(pref);
        setContractTypes(ctype);
        setContractUnits(cunit);
        setTemplateData(tmpl);
        setMasterLoaded(true);
      })
      .catch(() => errorDefault('マスタデータの取得に失敗しました'));
  };

  // 編集モード開始
  const handleStartEdit = () => {
    loadMasterData().then(() => {
      setEditPersonInChargeId(detail.personInCharge.current.userId);
      setEditPrefectureId(detail.prefecture.current.id || '');
      setEditLocation(detail.location.current || '');
      setEditContractTypeId(detail.contractType.current.id || '');
      setEditContractEndDate(detail.contractEndDate.current || '');
      setEditCostAmount(String(detail.costAmount.current));
      setEditSalesAmount(String(detail.salesAmount.current));
      setEditOperationRate(String(detail.operationRate.current));
      setEditContractUnitId(detail.contractUnit.current.id || '');
      setEditAdjustmentUpperLimit(
        detail.adjustmentUpperLimit.current != null
          ? String(detail.adjustmentUpperLimit.current)
          : '',
      );
      setEditAdjustmentLowerLimit(
        detail.adjustmentLowerLimit.current != null
          ? String(detail.adjustmentLowerLimit.current)
          : '',
      );
      setEditNeedsOriginSheet(detail.needsOriginSheet.current);
      setEditOriginSheetDestination(detail.originSheetDestination.current || '');
      setEditRemarks(detail.remarks.current || '');
      // テンプレート項目初期化
      const tmplVals: Record<string, string> = {};
      if (detail.contractItems) {
        Object.entries(detail.contractItems).forEach(([key, item]) => {
          if (item.currentValues && item.currentValues.length > 0) {
            tmplVals[key] =
              item.currentValues.length === 1
                ? item.currentValues[0]
                : JSON.stringify(item.currentValues);
          }
        });
      }
      setEditTemplateValues(tmplVals);
      // 技術スタックIDの初期化
      const techIds: string[] = [];
      const skillGroup = templateData?.results.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
      if (skillGroup && detail.contractItems) {
        skillGroup.items.forEach((item) => {
          const contractItem = detail.contractItems[item.itemKey];
          if (contractItem?.currentValues) {
            techIds.push(...contractItem.currentValues);
          }
        });
      }
      setEditTechStackIds(techIds);
      setIsEditMode(true);
    });
  };

  // 編集キャンセル
  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  // パラメータ構築
  const buildChangeParams = (
    requestStatus: 'ADJUSTING' | 'SUBMITTED',
    confirmData?: ConfirmDialogResult,
  ): ChangeContractRequestParams => {
    const templateItems: TemplateItemInput[] = Object.entries(editTemplateValues)
      .filter(([, v]) => v)
      .map(([key, value]) => {
        let values: string[];
        try {
          const parsed = JSON.parse(value);
          values = Array.isArray(parsed) ? parsed : [value];
        } catch {
          values = [value];
        }
        return { itemKey: key, values };
      });

    // 技術スタックIDをテンプレート項目に追加
    if (editTechStackIds.length > 0) {
      const skillGroup = templateGroups.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
      if (skillGroup && skillGroup.items.length > 0) {
        skillGroup.items.forEach((item) => {
          templateItems.push({ itemKey: item.itemKey, values: editTechStackIds });
        });
      }
    }

    return {
      contractId: detail.contractId,
      requestStatus,
      personInChargeId: editPersonInChargeId || detail.personInCharge.current.userId,
      prefectureId: editPrefectureId || undefined,
      location: editLocation || undefined,
      contractTypeId: editContractTypeId || undefined,
      contractEndDate: editContractEndDate || undefined,
      costAmount: editCostAmount ? Number(editCostAmount) : undefined,
      salesAmount: editSalesAmount ? Number(editSalesAmount) : undefined,
      operationRate: editOperationRate ? Number(editOperationRate) : undefined,
      contractUnitId: editContractUnitId || undefined,
      adjustmentUpperLimit: editAdjustmentUpperLimit ? Number(editAdjustmentUpperLimit) : undefined,
      adjustmentLowerLimit: editAdjustmentLowerLimit ? Number(editAdjustmentLowerLimit) : undefined,
      needsOriginSheet: editNeedsOriginSheet,
      originSheetDestination: editOriginSheetDestination || undefined,
      remarks: editRemarks || undefined,
      templateItems: templateItems.length > 0 ? templateItems : undefined,
      validStartDate: confirmData?.validStartDate || undefined,
      applicantUserIds: confirmData?.applicantUserIds,
      destinationUserIds: confirmData?.destinationUserIds,
    };
  };

  // 一時保存
  const handleSave = () => {
    const params = buildChangeParams('ADJUSTING');
    changeContractRequest(params)
      .then(() => {
        successDefault('一時保存しました');
        setIsEditMode(false);
        onUpdated();
      })
      .catch((e: Error) => errorDefault('一時保存に失敗しました', e.message));
  };

  // 申請ダイアログ表示
  const handleSubmitClick = () => {
    setChangeConfirmOpen(true);
  };

  // 申請確定
  const handleChangeConfirm = (data: ConfirmDialogResult) => {
    setChangeConfirmOpen(false);
    const params = buildChangeParams('SUBMITTED', data);
    changeContractRequest(params)
      .then(() => {
        successDefault('契約変更申請を提出しました');
        setIsEditMode(false);
        onUpdated();
      })
      .catch((e: Error) => errorDefault('契約変更申請に失敗しました', e.message));
  };

  // セレクトオプション
  const prefectureOptions: SelectOption[] = useMemo(
    () => prefectures.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [prefectures],
  );
  const contractTypeOptions: SelectOption[] = useMemo(
    () => contractTypes.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [contractTypes],
  );
  const contractUnitOptions: SelectOption[] = useMemo(
    () => contractUnits.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [contractUnits],
  );

  // テンプレートグループ変換
  const templateGroups: TemplateGroup[] = useMemo(() => {
    if (!templateData) return [];
    return templateData.results
      .filter((g) => g.sortOrder >= 0 || g.groupId === 'FIXED_MEMBER_SKILL')
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((g) => ({
        groupId: g.groupId,
        name: g.name,
        items: g.items
          .filter((i) => !i.isDeleted)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((i) => ({
            itemKey: i.itemKey,
            label: i.label,
            formType: i.formType,
            selectItems: i.selectItems
              .filter((si) => !si.isDeleted)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((si) => ({ code: si.code, name: si.name })),
          })),
      }));
  }, [templateData]);

  // 閲覧モード用: contractItemsから技術スタックIDを抽出
  const viewTechStackIds: string[] = useMemo(() => {
    if (!detail.contractItems || !templateData) return [];
    const skillGroup = templateData.results.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
    if (!skillGroup) return [];
    const ids: string[] = [];
    skillGroup.items.forEach((item) => {
      const contractItem = detail.contractItems[item.itemKey];
      if (contractItem?.currentValues) {
        ids.push(...contractItem.currentValues);
      }
    });
    return ids;
  }, [detail.contractItems, templateData]);

  return (
    <Stack spacing={2}>
      {/* ヘッダー（固定） */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
          mx: -2,
          px: 2,
          pt: 1,
          mt: -1,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight="bold">
              {detail.managementId}
            </Typography>
            <Chip
              size="small"
              label={ContractStatus[detail.status]}
              color={detail.status === 'ACTIVE' ? 'success' : 'default'}
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
            {detail.hasPendingRequest && (
              <Chip
                size="small"
                label="進行中の申請あり"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
          </Stack>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              作成者: {detail.createdByName} 作成日: {formatDate(detail.createdAt)}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              更新者: {detail.updatedByName} 更新日: {formatDate(detail.updatedAt)}
            </Typography>
          </Box>
        </Stack>

        {/* アクションボタン */}
        {isEditMode ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSave}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              一時保存
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmitClick}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              申請
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={handleCancelEdit}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              キャンセル
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1 }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {canChange && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleStartEdit}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  契約内容変更
                </Button>
              )}
              {canTerminate && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setTerminateDialogOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  契約終了
                </Button>
              )}
            </Stack>
            <ContractPasteButton data={detail} dataType="contract" />
          </Stack>
        )}
      </Box>

      {/* 基本情報 */}
      <DetailSection title="基本情報">
        <Grid container spacing={1.5}>
          {isEditMode ? (
            <>
              <Grid size={6}>
                <UserSelect
                  mode="single"
                  users={users}
                  selectedUserId={editPersonInChargeId}
                  onChange={(id) => setEditPersonInChargeId(id)}
                  label="担当者"
                />
              </Grid>
              <DetailField label="課長" value={detail.manager.userName} asInput />
              <DetailField label="プロジェクト" value={detail.project.projectName} asInput />
              <DetailField label="取引先" value={detail.client.clientName} asInput />
            </>
          ) : (
            <>
              <PendingField
                label="担当者"
                current={detail.personInCharge.current.userName}
                pending={detail.personInCharge.pending?.userName}
              />
              <DetailField label="課長" value={detail.manager.userName} />
              <DetailField label="プロジェクト" value={detail.project.projectName} />
              <DetailField label="取引先" value={detail.client.clientName} />
            </>
          )}
        </Grid>
      </DetailSection>

      {/* メンバー情報 */}
      <DetailSection title="メンバー情報">
        <Grid container spacing={1.5}>
          {isEditMode ? (
            <>
              <DetailField label="メンバー" value={detail.member.memberName} asInput />
              <DetailField label="パートナー" value={detail.partner.partnerName} asInput />
              <Grid size={6}>
                <CustomSelect
                  mode="single"
                  options={prefectureOptions}
                  value={editPrefectureId}
                  onChange={(v) => setEditPrefectureId(v)}
                  label="都道府県"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="ロケーション"
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                />
              </Grid>
            </>
          ) : (
            <>
              <DetailField label="メンバー" value={detail.member.memberName} />
              <DetailField label="パートナー" value={detail.partner.partnerName} />
              <PendingField
                label="都道府県"
                current={detail.prefecture.current.name}
                pending={detail.prefecture.pending?.name}
              />
              <PendingField
                label="ロケーション"
                current={detail.location.current || '—'}
                pending={detail.location.pending}
              />
            </>
          )}
        </Grid>
      </DetailSection>

      {/* 契約情報 */}
      <DetailSection title="契約情報">
        <Grid container spacing={1.5}>
          {isEditMode ? (
            <>
              <Grid size={6}>
                <CustomSelect
                  mode="single"
                  options={contractTypeOptions.filter((o) => o.value !== 'EMPLOYEE')}
                  value={editContractTypeId}
                  onChange={(v) => setEditContractTypeId(v)}
                  label="契約形態"
                />
              </Grid>
              <Grid size={6}>
                <CustomSelect
                  mode="single"
                  options={contractUnitOptions}
                  value={editContractUnitId}
                  onChange={(v) => setEditContractUnitId(v)}
                  label="契約単位"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="契約開始日"
                  value={formatDate(detail.contractStartDate.current)}
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value={editContractEndDate}
                  onChange={setEditContractEndDate}
                  label="契約終了日"
                />
              </Grid>
              <Grid size={6}>
                <AmountField
                  value={editCostAmount}
                  onChange={setEditCostAmount}
                  label="契約金額（原価）"
                />
              </Grid>
              <Grid size={6}>
                <AmountField
                  value={editSalesAmount}
                  onChange={setEditSalesAmount}
                  label="契約金額（売上）"
                />
              </Grid>
              <Grid size={6}>
                <PercentField
                  value={editOperationRate}
                  onChange={setEditOperationRate}
                  label="稼働率"
                />
              </Grid>
              <Grid size={6} />
              <Grid size={6}>
                <HourField
                  value={editAdjustmentUpperLimit}
                  onChange={setEditAdjustmentUpperLimit}
                  label="精算幅（上限）"
                />
              </Grid>
              <Grid size={6}>
                <HourField
                  value={editAdjustmentLowerLimit}
                  onChange={setEditAdjustmentLowerLimit}
                  label="精算幅（下限）"
                />
              </Grid>
            </>
          ) : (
            <>
              <PendingField
                label="契約形態"
                current={detail.contractType.current.name}
                pending={detail.contractType.pending?.name}
              />
              <PendingField
                label="契約単位"
                current={detail.contractUnit.current.name}
                pending={detail.contractUnit.pending?.name}
              />
              <PendingField
                label="契約開始日"
                current={formatDate(detail.contractStartDate.current)}
                pending={
                  detail.contractStartDate.pending
                    ? formatDate(detail.contractStartDate.pending)
                    : undefined
                }
              />
              <PendingField
                label="契約終了日"
                current={formatDate(detail.contractEndDate.current)}
                pending={
                  detail.contractEndDate.pending
                    ? formatDate(detail.contractEndDate.pending)
                    : undefined
                }
              />
              <PendingField
                label="契約金額（原価）"
                current={`${detail.costAmount.current.toLocaleString()}円`}
                pending={
                  detail.costAmount.pending != null
                    ? `${detail.costAmount.pending.toLocaleString()}円`
                    : undefined
                }
              />
              <PendingField
                label="契約金額（売上）"
                current={`${detail.salesAmount.current.toLocaleString()}円`}
                pending={
                  detail.salesAmount.pending != null
                    ? `${detail.salesAmount.pending.toLocaleString()}円`
                    : undefined
                }
              />
              <PendingField
                label="稼働率"
                current={`${detail.operationRate.current}%`}
                pending={
                  detail.operationRate.pending != null
                    ? `${detail.operationRate.pending}%`
                    : undefined
                }
              />
              <Grid size={6} />
              <PendingField
                label="精算幅（上限）"
                current={
                  detail.adjustmentUpperLimit.current != null
                    ? `${detail.adjustmentUpperLimit.current}h`
                    : '—'
                }
                pending={
                  detail.adjustmentUpperLimit.pending != null
                    ? `${detail.adjustmentUpperLimit.pending}h`
                    : undefined
                }
              />
              <PendingField
                label="精算幅（下限）"
                current={
                  detail.adjustmentLowerLimit.current != null
                    ? `${detail.adjustmentLowerLimit.current}h`
                    : '—'
                }
                pending={
                  detail.adjustmentLowerLimit.pending != null
                    ? `${detail.adjustmentLowerLimit.pending}h`
                    : undefined
                }
              />
            </>
          )}
        </Grid>
      </DetailSection>

      {/* 補足情報 */}
      <DetailSection title="補足情報">
        <Grid container spacing={1.5}>
          {isEditMode ? (
            <>
              <Grid size={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editNeedsOriginSheet}
                      onChange={(e) => setEditNeedsOriginSheet(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">原籍確認シート: 必要</Typography>}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="提出先"
                  value={editOriginSheetDestination}
                  onChange={(e) => setEditOriginSheetDestination(e.target.value)}
                  disabled={!editNeedsOriginSheet}
                />
              </Grid>
            </>
          ) : (
            <>
              <PendingField
                label="原籍確認シート"
                current={detail.needsOriginSheet.current ? '必要' : '不要'}
                pending={
                  detail.needsOriginSheet.pending != null
                    ? detail.needsOriginSheet.pending
                      ? '必要'
                      : '不要'
                    : undefined
                }
              />
              <PendingField
                label="提出先"
                current={detail.originSheetDestination.current || '—'}
                pending={detail.originSheetDestination.pending}
              />
            </>
          )}
        </Grid>
      </DetailSection>

      {/* 特記事項 */}
      {isEditMode ? (
        <DetailSection title="特記事項">
          <TextArea
            value={editRemarks}
            onChange={setEditRemarks}
            maxLength={10000}
            rows={4}
            fullWidth
          />
        </DetailSection>
      ) : (
        <DetailSection title="特記事項">
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {detail.remarks.current || '—'}
          </Typography>
        </DetailSection>
      )}

      {/* テンプレート項目 */}
      {isEditMode && templateGroups.length > 0 && (
        <TemplateGroupView
          groups={templateGroups}
          mode="edit"
          values={editTemplateValues}
          onValueChange={(key, val) => setEditTemplateValues((prev) => ({ ...prev, [key]: val }))}
          techStackIds={editTechStackIds}
          onTechStackChange={setEditTechStackIds}
        />
      )}
      {!isEditMode && detail.contractItems && Object.keys(detail.contractItems).length > 0 && (
        <TemplateGroupView
          groups={templateGroups}
          mode="preview"
          values={Object.fromEntries(
            Object.entries(detail.contractItems).map(([key, item]) => [
              key,
              item.currentValues.length === 1
                ? item.currentValues[0]
                : JSON.stringify(item.currentValues),
            ]),
          )}
          diffValues={Object.fromEntries(
            Object.entries(detail.contractItems)
              .filter(([, item]) => item.pendingValues != null)
              .map(([key, item]) => [
                key,
                item.pendingValues!.length === 1
                  ? item.pendingValues![0]
                  : JSON.stringify(item.pendingValues),
              ]),
          )}
          techStackIds={viewTechStackIds}
          onValueChange={() => {}}
        />
      )}

      {/* 契約終了ダイアログ */}
      <ContractConfirmDialog
        open={terminateDialogOpen}
        onClose={() => setTerminateDialogOpen(false)}
        onConfirm={handleTerminateConfirm}
        type="terminate"
        managers={managers}
        backOfficeUsers={backOfficeUsers}
        currentUserId={userSession.userId}
        contractEndDate={detail.contractEndDate.current}
      />

      {/* 契約内容変更申請ダイアログ */}
      <ContractConfirmDialog
        open={changeConfirmOpen}
        onClose={() => setChangeConfirmOpen(false)}
        onConfirm={handleChangeConfirm}
        type="change"
        managers={managers}
        backOfficeUsers={backOfficeUsers}
        currentUserId={userSession.userId}
      />
    </Stack>
  );
};

// --- DetailSection ---
const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const [expanded, setExpanded] = useState(true);

  const handleToggle = () => setExpanded((prev) => !prev);

  // 目次（FormTocLayout）用のアンカー。タイトルから id を生成する。
  const tocId = `sec-${encodeURIComponent(title)}`;

  return (
    <Accordion
      id={tocId}
      data-toc-section
      data-toc-label={title}
      elevation={0}
      expanded={expanded}
      sx={{
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        scrollMarginTop: 8,
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton size="small" onClick={handleToggle} sx={{ cursor: 'pointer' }}>
            <ExpandMoreIcon />
          </IconButton>
        }
        sx={{
          px: 2,
          minHeight: 40,
          flexDirection: 'row-reverse',
          gap: 1,
          cursor: 'default',
          '&.Mui-focusVisible': { bgcolor: 'transparent' },
          '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none' },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'none' },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          onClick={handleToggle}
          sx={{ cursor: 'pointer' }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

// --- DetailField ---
const DetailField: React.FC<{ label: string; value: string; asInput?: boolean }> = ({
  label,
  value,
  asInput = false,
}) =>
  asInput ? (
    <Grid size={6}>
      <TextField
        size="small"
        fullWidth
        label={label}
        value={value}
        disabled
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Grid>
  ) : (
    <Grid size={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );

// --- PendingField (current/pending差分表示) ---
const PendingField: React.FC<{ label: string; current: string; pending?: string | null }> = ({
  label,
  current,
  pending,
}) => {
  const hasPending = pending != null && pending !== current;
  return (
    <Grid size={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {hasPending ? (
        <Typography
          variant="body2"
          sx={{
            color: 'success.main',
            fontWeight: 600,
          }}
        >
          {pending}
        </Typography>
      ) : (
        <Typography variant="body2">{current}</Typography>
      )}
    </Grid>
  );
};

export default ContractDetail;
